import { Meeting } from '@/types/Meeting';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import { Tooltip, TooltipContent, TooltipTrigger } from './ui/tooltip';

interface MeetingListItemProps {
  meeting: Meeting;
}

export function MeetingListItem({ meeting }: MeetingListItemProps) {
  const navigate = useNavigate();

  // Get action stats from backend
  const totalActions = meeting.actionStats?.total || 0;
  const completedActions = meeting.actionStats?.completed || 0;
  const completionPercent = totalActions > 0 
    ? Math.round((completedActions / totalActions) * 100) 
    : 0;

  // Determine if current user is owner
  const ownerEmail = typeof meeting.owner === 'string' ? meeting.owner : meeting.owner?.email;

  return (
    <div
      onClick={() => navigate(`/app/meetings/${meeting.id}`)}
      className="group glass-panel rounded-xl p-3 cursor-pointer transition-all duration-300 hover:shadow-glass-hover hover:-translate-y-0.5"
    >
      <div className="flex items-center justify-between gap-4">
        {/* Date */}
        <div className="text-sm text-muted-foreground dark:text-slate-400 min-w-[100px]">
          {format(new Date(meeting.date), 'EEE dd/MM/yyyy')}
        </div>

        {/* Title */}
        <div className="font-medium flex-1 text-slate-800 dark:text-slate-100 group-hover:text-fluid-primary dark:group-hover:text-indigo-400 transition-colors">
          {meeting.title}
        </div>

        {/* Action Progress (if actions exist) */}
        {totalActions > 0 && (
          <div className="hidden md:flex items-center gap-2 min-w-[260px]">
            <svg className="w-3 h-3 text-fluid-primary flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-bold text-slate-600 dark:text-slate-300 whitespace-nowrap">
              Actions Completed
            </span>
            <div className="flex-1 bg-white/50 dark:bg-slate-700/50 rounded-full h-1.5 overflow-hidden min-w-[60px]">
              <div 
                className="h-full rounded-full bg-gradient-to-r from-fluid-primary to-fluid-accent transition-all duration-500" 
                style={{ width: `${completionPercent}%` }}
              />
            </div>
            <span className="text-xs font-semibold text-slate-600 dark:text-slate-300 min-w-[60px] text-right">
              {completionPercent}% ({completedActions}/{totalActions})
            </span>
          </div>
        )}

        {/* Owner Avatar with Tooltip */}
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="hidden sm:flex items-center gap-2 cursor-pointer">
              <div className="w-7 h-7 rounded-full bg-gradient-to-tr from-fluid-primary to-fluid-accent flex items-center justify-center text-white font-bold text-[10px] border-2 border-white shadow-sm">
                {ownerEmail?.substring(0, 2).toUpperCase() || 'ME'}
              </div>
            </div>
          </TooltipTrigger>
          <TooltipContent>
            <p>{ownerEmail}</p>
          </TooltipContent>
        </Tooltip>
      </div>
    </div>
  );
}
